"use client";

import { useCallback, useState, type ReactNode } from "react";

import { ChangePlanModal } from "../components/change-plan-modal";
import { SubscribeModal } from "../components/subscribe-modal";
import { usePlans, useSubscription, type PlanResponse } from "./use-subscription";

/**
 * Plan-upgrade flow (pick a plan → subscribe) shared by every "Upgrade"
 * entry point. Returns whether the org is on the free plan, an opener, and
 * the modal tree the caller must mount once alongside its trigger.
 */
export function useUpgradePlanFlow(): {
  isFreePlan: boolean;
  isLoading: boolean;
  openUpgrade: () => void;
  modals: ReactNode;
} {
  const { data: subscription, isLoading } = useSubscription();
  const { data: plans } = usePlans();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [subscribeModal, setSubscribeModal] = useState<{
    plan: PlanResponse;
    cadence: "monthly" | "annual";
    seatCount: number;
  } | null>(null);

  const handleUpgrade = useCallback(
    (planSlug: string, cadence: "annual" | "monthly", seatCount: number) => {
      const selectedPlan = plans?.find((p) => p.slug === planSlug);
      if (!selectedPlan) return;
      setShowUpgrade(false);
      setSubscribeModal({ plan: selectedPlan, cadence, seatCount });
    },
    [plans],
  );

  const openUpgrade = useCallback(() => setShowUpgrade(true), []);

  const planSlug = subscription?.plan.slug ?? "free";
  const isFreePlan = !isLoading && planSlug === "free";

  const modals = (
    <>
      {plans && plans.length > 0 && (
        <ChangePlanModal
          open={showUpgrade}
          onOpenChange={setShowUpgrade}
          plans={plans}
          currentPlanSlug={planSlug}
          currentCadence="monthly"
          currentSeatCount={subscription?.seat_count ?? 1}
          upgradingSlug={null}
          onUpgrade={handleUpgrade}
          // Seat edits only exist for the current paid plan's card, which the
          // modal never renders on the free plan.
          onSeatChange={() => {}}
        />
      )}

      {subscribeModal && (
        <SubscribeModal
          open
          onClose={() => setSubscribeModal(null)}
          plan={subscribeModal.plan}
          initialCadence={subscribeModal.cadence}
          initialSeatCount={subscribeModal.seatCount}
        />
      )}
    </>
  );

  return { isFreePlan, isLoading, openUpgrade, modals };
}
